import { Component, ElementRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { NotificationService, NotificationItem } from '../../service/notification.service';

import { AuthenticationService } from '../../service/authentication.service';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss'
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private readonly notificationService = inject(NotificationService);
  private readonly authService = inject(AuthenticationService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  private readonly subscriptions = new Subscription();

  notifications: NotificationItem[] = [];
  unreadCount = 0;

  isOpen = false;
  isLoading = false;
  loadError = false;
  markingReadIds = new Set<number>();

  ngOnInit(): void {
    this.subscriptions.add(
      this.notificationService.notifications$.subscribe((items) => {
        this.notifications = items;
      })
    );

    this.subscriptions.add(
      this.notificationService.unreadCount$.subscribe((count) => {
        this.unreadCount = count;
      })
    );

    const token = this.authService.getToken();

    if (!token) {
      return;
    }

    // Connect for new real-time notifications.
    this.notificationService.connect(token);

    // Load existing unread notifications and show pending toasts.
    this.loadNotifications(true);
  }

  get latestNotifications(): NotificationItem[] {
    return [...this.notifications].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 5);
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }
  
  loadNotifications(showPendingToasts = false): void {
    this.isLoading = true;
    this.loadError = false;

    this.subscriptions.add(
      this.notificationService.loadNotifications(showPendingToasts).subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.loadError = true;

          console.error('Failed to load notifications:', error);
        }
      })
    );
  }

  dismissNotification(event: MouseEvent, item: NotificationItem): void {
    event.stopPropagation();

    this.subscriptions.add(
      this.notificationService.dismissNotification(item.notification_id).subscribe({
        error: (error) => {
          console.error('Failed to dismiss notification:', error);
        }
      })
    );
  }

  markAsRead(notification: NotificationItem): void {
    if (notification.is_read || this.markingReadIds.has(notification.notification_id)) {
      return;
    }

    this.markingReadIds.add(notification.notification_id);

    this.subscriptions.add(
      this.notificationService.markAsRead(notification.notification_id).subscribe({
        error: (error) => {
          console.error('Failed to mark notification as read:', error);
        },
        complete: () => {
          this.markingReadIds.delete(notification.notification_id);
        }
      })
    );
  }

  trackByNotification(_index: number, item: NotificationItem): number {
    return item.notification_id;
  }

  @HostListener('document:click', ['$event'])
  closeOnOutsideClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.notificationService.disconnect();
  }
}
